import {ConnectOptions, HttpRecoveryOptions, SSEHandlers, TokenProvider} from "./types";
import {SessionManager} from "./SessionManager";
import {SSEManager} from "./SSEManager";

export class ConnectionManager {
  private readonly sessionManager: SessionManager;
  private readonly sseManager: SSEManager;
  private readonly tokenProvider: TokenProvider;
  private readonly apiBaseUrl: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isReconnecting: boolean = false;
  private reconnectHandlers: SSEHandlers | null = null;
  private currentSessionId: string | null = null;

  private async buildStreamUrl(sessionId: string): Promise<string> {
    const tokenMaybe = await (typeof this.tokenProvider === 'function' ? (this.tokenProvider as any)() : this.tokenProvider);
    const token = tokenMaybe || '';
    return `${this.apiBaseUrl}/chat/session/${sessionId}/stream?token=${encodeURIComponent(token)}`;
  }

  constructor(
    params: {
      sessionManager: SessionManager;
      sseManager: SSEManager;
      tokenProvider: TokenProvider;
      apiBaseUrl: string;
    }
  ) {
    this.sessionManager = params.sessionManager;
    this.sseManager = params.sseManager;
    this.tokenProvider = params.tokenProvider;
    this.apiBaseUrl = params.apiBaseUrl;
  }

  public async connectSSEWithRecovery(sessionId: string, handlers: SSEHandlers, opts: ConnectOptions = {}): Promise<EventSource> {
    const attempts = opts.attempts ?? 1;
    const timeoutMs = opts.connectTimeoutMs ?? 5000;
    let lastErr: any;
    
    // Wrap error handler to trigger reconnection
    const wrappedHandlers: SSEHandlers = {
      ...handlers,
      onError: (err) => {
        // Only attempt reconnection if connection is actually closed
        const currentEs = this.sseManager.current;
        if (currentEs && currentEs.readyState === EventSource.CLOSED && !this.isReconnecting) {
          // Connection dropped - attempt reconnection
          this.attemptReconnection(sessionId, handlers).catch(console.error);
        }
        handlers.onError?.(err);
      }
    };
    
    for (let i = 0; i < attempts; i++) {
      try {
        const streamUrl = await this.buildStreamUrl(sessionId);
        const es = await this.sseManager.connect(streamUrl, sessionId, wrappedHandlers);
        
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(() => {
            return reject(new Error('SSE connect timeout'))
          }, timeoutMs);

          const onOpen = () => {
            clearTimeout(timer);
            // Reset reconnection attempts on successful connection
            this.reconnectAttempts = 0;
            resolve();
          };

          if (es.readyState === EventSource.OPEN) {
            clearTimeout(timer);
            this.reconnectAttempts = 0;
            resolve();
            return;
          }

          es.onopen = () => {
            wrappedHandlers.onOpen?.();
            onOpen();
          };
        });

        return es;
      } catch (e) {
        lastErr = e;
        await new Promise(r => setTimeout(r, Math.min(500 * (2 ** i), 15000)));
      }
    }

    throw lastErr;
  }

  public async getOrCreateSession(createPayload: any) {
    const session = await this.sessionManager.getOrCreate(createPayload);
    return session.session_id;
  }

  public isStreamConnected(sessionId?: string | null) {
    const sid = sessionId ?? this.sessionManager.sessionId;
    if (!sid) return false;
    const isConnected = this.sseManager.isConnectedTo(String(sid));
    const isHealthy = this.sseManager.isConnectionHealthy();
    return isConnected && isHealthy;
  }

  public closeAll() {
    this.stopReconnection();
    this.sseManager.close();
    this.sessionManager.invalidate();
    this.reconnectAttempts = 0;
    this.currentSessionId = null;
    this.reconnectHandlers = null;
  }

  private stopReconnection(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.isReconnecting = false;
  }

  public async attemptReconnection(sessionId: string, handlers: SSEHandlers): Promise<EventSource | null> {
    if (this.isReconnecting) {
      return null; // Already reconnecting
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`Max reconnection attempts (${this.maxReconnectAttempts}) reached for session ${sessionId}`);
      this.stopReconnection();
      handlers.onError?.(new Error('Max reconnection attempts reached'));
      return null;
    }

    this.isReconnecting = true;
    this.currentSessionId = sessionId;
    this.reconnectHandlers = handlers;
    this.reconnectAttempts++;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);

    console.log(`Attempting to reconnect (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}) after ${delay}ms...`);

    return new Promise((resolve) => {
      this.reconnectTimeout = setTimeout(async () => {
        try {
          const es = await this.connectSSEWithRecovery(sessionId, handlers, {
            attempts: 3,
            connectTimeoutMs: 10000
          });
          
          // Successfully reconnected - reset attempts
          this.reconnectAttempts = 0;
          this.isReconnecting = false;
          this.reconnectTimeout = null;
          console.log(`Successfully reconnected to session ${sessionId}`);
          resolve(es);
        } catch (error) {
          console.warn(`Reconnection attempt ${this.reconnectAttempts} failed:`, error);
          this.isReconnecting = false;
          
          // Try again if we haven't exceeded max attempts
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            const nextEs = await this.attemptReconnection(sessionId, handlers);
            resolve(nextEs);
          } else {
            this.stopReconnection();
            handlers.onError?.(error as Error);
            resolve(null);
          }
        }
      }, delay);
    });
  }

  public async httpWithRecovery<T>(opName: 'create' | 'send' | 'interrupt', fn: () => Promise<T>, opt: HttpRecoveryOptions = {}): Promise<T> {
    const attempts = opt.attempts ?? (opName === 'create' ? 2 : 1);
    const base = opt.baseDelayMs ?? 500;
    const max = opt.maxDelayMs ?? 15000;

    let sessionInvalidated = false;
    for (let i = 0; i < attempts; i++) {
      try {
        return await fn();
      } catch (err: any) {
        const status = err?.response?.status;
        const message: string = String(err?.message || '').toLowerCase();
        const looksSession = status === 401 
          || status === 403 
          || status === 404 
          || message.includes('session') 
          || message.includes('expired') 
          || message.includes('invalid');

        if (!sessionInvalidated && looksSession) {
          sessionInvalidated = true;
          this.sessionManager.invalidate();
          await opt.onSessionInvalid?.();
          continue;
        }

        const isBackend = status >= 500 && status < 600;
        const isNetwork = status === undefined;
        const isTimeout = message.includes('timeout');
        if (isBackend || isNetwork || isTimeout) {
          const delay = Math.min(base * (2 ** i) + Math.floor(Math.random() * base), max);
          await new Promise(r => setTimeout(r, delay));
          continue;
        }

        throw err;
      }
    }
    
    throw new Error(`${opName} failed after retries`);
  }
}